<template>
    <div class="card">
        <div class="card-header">
            <div>
                <h2>Detalhes do Protocolo</h2>
                <span>{{ solicitacao.protocolo }} - {{ solicitacao.estabelecimentoNome }}</span>
            </div>

            <span class="badge" :class="badgeClass(solicitacao.status)">
                {{ formatarStatus(solicitacao.status) }}
            </span>
        </div>

        <div class="bloco-grid">
            <div class="info-box">
                <h3>Dados da Solicitação</h3>
                <div class="info-list">
                    <div><strong>Tipo:</strong> {{ formatarTipo(solicitacao.tipoSolicitacao) }}</div>
                    <div><strong>Classificação:</strong> {{ solicitacao.classificacao }}</div>
                    <div><strong>Produto:</strong> {{ solicitacao.produtoNome }}</div>
                    <div><strong>Versão:</strong> {{ solicitacao.produtoVersao || '-' }}</div>
                    <div><strong>Responsável:</strong> {{ solicitacao.responsavel }}</div>
                    <div><strong>Data:</strong> {{ formatarData(solicitacao.dataSolicitacao) }}</div>
                </div>
            </div>

            <div class="info-box">
                <h3>Observações</h3>
                <p class="obs-text">
                    {{ solicitacao.observacoes || 'Sem observações complementares.' }}
                </p>
            </div>
        </div>

        <div class="bloco-grid">
            <div class="info-box">
                <h3>Anexos</h3>
                <div v-if="solicitacao.anexos?.length" class="anexos-lista">
                    <div class="anexo-item" v-for="anexo in solicitacao.anexos" :key="anexo.id + anexo.nome">
                        <span>{{ anexo.nome }}</span>
                        <small>{{ anexo.tipo }}</small>
                    </div>
                </div>
                <p v-else class="empty-text">Nenhum anexo informado.</p>
            </div>

            <div class="info-box">
                <h3>Pendências</h3>
                <div v-if="solicitacao.pendencias?.length" class="pendencias-lista">
                    <div class="pendencia-item" v-for="item in solicitacao.pendencias" :key="item.id">
                        {{ item.descricao }}
                    </div>
                </div>
                <p v-else class="empty-text">Sem pendências no momento.</p>
            </div>
        </div>

        <div class="info-box">
            <h3>Histórico / Acompanhamento</h3>
            <div class="timeline">
                <div
                    class="timeline-item"
                    v-for="(item, index) in solicitacao.historico"
                    :key="index"
                >
                    <div class="timeline-dot"></div>
                    <div class="timeline-content">
                        <div class="timeline-date">{{ item.data }}</div>
                        <div class="timeline-event">{{ item.evento }}</div>
                    </div>
                </div>
            </div>
        </div>

        <div class="actions">
            <button type="button" class="btn btn-light">Registrar Exigência</button>
            <button type="button" class="btn btn-light">Aprovar</button>
            <button type="button" class="btn btn-primary">Aprovar e Gerar Cadastro no Item 18</button>
        </div>
    </div>
</template>

<script setup>
defineProps({
    solicitacao: { type: Object, required: true }
})

function formatarTipo(tipo) {
    const mapa = {
        NOVO_REGISTRO: 'Novo registro',
        ALTERACAO: 'Alteração',
        CANCELAMENTO: 'Cancelamento'
    }
    return mapa[tipo] || tipo
}

function formatarStatus(status) {
    const mapa = {
        EM_ANALISE: 'Em análise',
        COM_PENDENCIA: 'Com pendência',
        APROVADO: 'Aprovado',
        REPROVADO: 'Reprovado'
    }
    return mapa[status] || status
}

function badgeClass(status) {
    if (status === 'APROVADO') return 'success'
    if (status === 'COM_PENDENCIA') return 'warning'
    if (status === 'REPROVADO') return 'danger'
    return 'info'
}

function formatarData(data) {
    if (!data) return ''
    const [ano, mes, dia] = data.split('-')
    return `${dia}/${mes}/${ano}`
}
</script>

<style scoped>
.card {
    background: #ffffff;
    border: 1px solid #dfe4ea;
    border-radius: 10px;
    padding: 18px;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
}

.card-header h2 {
    margin: 0;
    font-size: 18px;
    color: #1f2937;
}

.card-header span {
    display: block;
    margin-top: 4px;
    color: #6b7280;
}

.badge {
    display: inline-block;
    padding: 6px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
}

.badge.success {
    background: #dcfce7;
    color: #166534;
}

.badge.warning {
    background: #fef3c7;
    color: #92400e;
}

.badge.danger {
    background: #fee2e2;
    color: #991b1b;
}

.badge.info {
    background: #dbeafe;
    color: #1d4ed8;
}

.bloco-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-bottom: 14px;
}

.info-box {
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 14px;
    background: #fafafa;
}

.info-box h3 {
    margin: 0 0 12px 0;
    font-size: 15px;
    color: #111827;
}

.info-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    color: #374151;
    font-size: 14px;
}

.obs-text {
    margin: 0;
    color: #374151;
    line-height: 1.5;
}

.anexos-lista,
.pendencias-lista {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.anexo-item,
.pendencia-item {
    padding: 10px 12px;
    border-radius: 8px;
    background: #eef2f7;
}

.anexo-item span {
    display: block;
    font-size: 14px;
    color: #111827;
}

.anexo-item small {
    color: #6b7280;
}

.empty-text {
    margin: 0;
    color: #6b7280;
}

.timeline {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.timeline-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.timeline-dot {
    width: 12px;
    height: 12px;
    margin-top: 4px;
    border-radius: 50%;
    background: #2563eb;
    flex-shrink: 0;
}

.timeline-content {
    flex: 1;
    border-left: 2px solid #dbeafe;
    padding-left: 12px;
}

.timeline-date {
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 3px;
}

.timeline-event {
    font-size: 14px;
    color: #111827;
}

.actions {
    margin-top: 18px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    flex-wrap: wrap;
}

.btn {
    border: none;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 14px;
    cursor: pointer;
}

.btn-light {
    background: #e5e7eb;
    color: #111827;
}

.btn-primary {
    background: #2563eb;
    color: #ffffff;
}

@media (max-width: 960px) {
    .bloco-grid {
        grid-template-columns: 1fr;
    }

    .card-header {
        flex-direction: column;
    }
}
</style>